import {
  useCallback,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';

/**
 * Small ⓘ button with a hover/focus tooltip. The bubble is rendered through a
 * portal to <body> so it escapes any ancestor `overflow: hidden` / `clip`.
 * Position is computed from the trigger's bounding rect on open and on scroll
 * / resize while open.
 */
export function InfoHint({
  text,
  label = 'More info',
  side = 'top',
}: {
  text: ReactNode;
  label?: string;
  side?: 'top' | 'bottom' | 'right';
}) {
  const id = useId();
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const [pos, setPos] = useState<{ top: number; left: number; transform: string }>({
    top: 0,
    left: 0,
    transform: '',
  });

  const reposition = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const cx = r.left + r.width / 2;
    const cy = r.top + r.height / 2;
    if (side === 'right') {
      setPos({ top: cy, left: r.right + 8, transform: 'translateY(-50%)' });
    } else if (side === 'bottom') {
      setPos({ top: r.bottom + 6, left: cx, transform: 'translateX(-50%)' });
    } else {
      setPos({ top: r.top - 6, left: cx, transform: 'translate(-50%, -100%)' });
    }
  }, [side]);

  useLayoutEffect(() => {
    if (!open) return;
    reposition();
    const onScroll = () => reposition();
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [open, reposition]);

  const wrap: CSSProperties = {
    position: 'relative',
    display: 'inline-flex',
    marginLeft: 'var(--sp-1)',
    verticalAlign: 'middle',
  };

  const trigger: CSSProperties = {
    width: 16,
    height: 16,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',
    border: '1px solid var(--border-subtle, rgba(255,255,255,0.28))',
    background: open ? 'var(--surface-2, rgba(255,255,255,0.08))' : 'transparent',
    color: open ? 'var(--text-primary)' : 'var(--text-muted)',
    cursor: 'pointer',
    fontSize: 11,
    lineHeight: 1,
    fontWeight: 700,
    fontFamily: 'Georgia, "Times New Roman", serif',
    fontStyle: 'italic',
    padding: 0,
    userSelect: 'none',
  };

  const bubble: CSSProperties = {
    position: 'fixed',
    top: pos.top,
    left: pos.left,
    transform: pos.transform,
    zIndex: 10_000,
    minWidth: 220,
    maxWidth: 320,
    width: 'max-content',
    padding: 'var(--sp-2) var(--sp-3)',
    background: 'var(--surface-1, #1a1a1a)',
    color: 'var(--text-primary, #fff)',
    border: '1px solid var(--border-subtle, rgba(255,255,255,0.18))',
    borderRadius: 'var(--radius-sm, 4px)',
    boxShadow: '0 8px 24px rgba(0,0,0,0.48)',
    fontSize: 12,
    lineHeight: 1.45,
    fontWeight: 400,
    fontStyle: 'normal',
    fontFamily: 'inherit',
    whiteSpace: 'normal',
    pointerEvents: 'none',
  };

  return (
    <span style={wrap} onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <button
        ref={triggerRef}
        type="button"
        aria-label={label}
        aria-describedby={open ? id : undefined}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onClick={(e) => {
          e.preventDefault();
          setOpen((v) => !v);
        }}
        style={trigger}
      >
        i
      </button>
      {open && typeof document !== 'undefined'
        ? createPortal(
            <span id={id} role="tooltip" style={bubble}>
              {text}
            </span>,
            document.body,
          )
        : null}
    </span>
  );
}
